import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminDashboard } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Library, FilePlus2, Landmark, Newspaper, Users, Boxes, CalendarCheck,
  ArrowRight, FileText, Inbox, MessageCircleQuestion, Activity, Database,
  AlertTriangle, Megaphone, PenLine, Type, ListChecks, ImageOff, CircleAlert,
} from "lucide-react";

const fmt = (d) => { try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d; } };

const ACTION_LABEL = {
  created: "created", created_draft: "drafted", updated: "updated", published: "published",
  deleted: "deleted", role_changed: "changed role of", banned: "banned", unbanned: "unbanned",
  user_deleted: "deleted user", signed_up: "signed up", booking_requested: "requested booking",
  note_added: "noted on", changelog_added: "posted changelog", waitlist_joined: "joined waitlist",
};

const AttnCard = ({ to, icon: Icon, value, label, tone = "saffron", testid }) => (
  <Link to={to} data-testid={testid} className="group flex items-center gap-4 border border-line bg-surface p-5 transition-colors hover:border-saffron">
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center border ${value > 0 ? "border-saffron text-saffron" : "border-line text-ink-3"}`}>
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <p className="font-serif-latin text-2xl text-ink">{value ?? "…"}</p>
      <p className="text-xs text-ink-3">{label}</p>
    </div>
    <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-saffron" />
  </Link>
);

const QUICK = [
  { to: "/admin/content", icon: Library, label: "Content Library" },
  { to: "/admin/content/new", icon: FilePlus2, label: "Add Content" },
  { to: "/admin/collections", icon: Landmark, label: "Sections & Hub" },
  { to: "/admin/journal", icon: Newspaper, label: "Patrika" },
  { to: "/admin/changelog", icon: Megaphone, label: "Changelog" },
  { to: "/admin/headings", icon: Type, label: "Page Titles" },
  { to: "/admin/activity", icon: Activity, label: "Activity Log" },
  { to: "/admin/waitlist", icon: ListChecks, label: "Waitlist" },
  { to: "/admin/users", icon: Users, label: "Users & Roles" },
  { to: "/admin/services", icon: Boxes, label: "Services" },
  { to: "/admin/data", icon: Database, label: "Data & Health" },
  { to: "/admin/seo", icon: FileText, label: "SEO" },
];

export default function AdminOverview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { adminDashboard().then(setD).catch(() => setErr(true)); }, []);

  const p = d?.pending || {};
  const c = d?.content || {};
  const u = d?.users || {};
  const h = d?.health || {};

  return (
    <div data-testid="admin-overview">
      <div className="mb-8">
        <p className="overline-label text-gold">Console</p>
        <h1 className="mt-1 font-serif-latin text-3xl text-ink">Namaste{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
        <p className="mt-1 text-sm text-ink-3">Here's what needs your attention on Dharmartha today.</p>
      </div>

      {/* Needs attention */}
      <div className="mb-4 flex items-center gap-3">
        <span className="h-px w-8 bg-[var(--gold)]" />
        <h2 className="overline-label text-ink-3">Needs attention</h2>
      </div>
      <div className="mb-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AttnCard testid="attn-drafts" to="/admin/content?status=draft" icon={PenLine} value={p.drafts} label="Drafts to review" />
        <AttnCard testid="attn-bookings" to="/admin/bookings" icon={CalendarCheck} value={p.bookings} label="New bookings" />
        <AttnCard testid="attn-messages" to="/admin/messages" icon={Inbox} value={p.messages} label="Unread messages" />
        <AttnCard testid="attn-questions" to="/admin/community" icon={MessageCircleQuestion} value={p.questions} label="Unanswered questions" />
      </div>

      {err && (
        <div className="mb-10 flex items-center gap-3 border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-ink-2">
          <CircleAlert className="h-5 w-5 shrink-0 text-amber-600" /> Live dashboard metrics need an admin account. Use the quick links below to manage the site.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent activity */}
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center gap-3">
            <Activity className="h-4 w-4 text-saffron" />
            <h2 className="overline-label text-ink-3">Recent activity</h2>
            <Link to="/admin/activity" className="ml-auto text-xs text-saffron hover:underline">View all</Link>
          </div>
          <div className="border border-line bg-surface" data-testid="dash-activity">
            {d === null && !err ? (
              <p className="py-14 text-center text-ink-3">…</p>
            ) : (d?.recent_activity || []).length === 0 ? (
              <p className="py-14 text-center text-sm text-ink-3">No activity yet.</p>
            ) : (
              <ul className="divide-y divide-line">
                {d.recent_activity.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 px-4 py-3 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-saffron" />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink-2">
                        <span className="font-medium text-ink">{a.actor_name}</span> {ACTION_LABEL[a.action] || a.action}{" "}
                        {a.target && <span className="font-medium text-ink">{a.target}</span>}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-3">{fmt(a.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right column: health + drafts */}
        <div className="space-y-6">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <AlertTriangle className="h-4 w-4 text-saffron" />
              <h2 className="overline-label text-ink-3">Site health</h2>
            </div>
            <div className="space-y-3 border border-line bg-surface p-5" data-testid="dash-health">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-ink-2"><Library className="h-4 w-4 text-ink-3" /> Published</span>
                <span className="font-serif-latin text-ink">{c.published ?? "…"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-ink-2"><PenLine className="h-4 w-4 text-ink-3" /> Drafts</span>
                <span className="font-serif-latin text-ink">{c.drafts ?? "…"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-ink-2"><CircleAlert className="h-4 w-4 text-ink-3" /> Duplicate slugs</span>
                <span className={`font-serif-latin ${h.duplicate_slugs > 0 ? "text-amber-600" : "text-ink"}`}>{h.duplicate_slugs ?? "…"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-ink-2"><ImageOff className="h-4 w-4 text-ink-3" /> Missing image</span>
                <span className="font-serif-latin text-ink">{h.missing_image ?? "…"}</span>
              </div>
              <Link to="/admin/data" className="mt-2 inline-flex items-center gap-1 text-xs text-saffron hover:underline">Open Data &amp; Health <ArrowRight className="h-3 w-3" /></Link>
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-3">
              <Users className="h-4 w-4 text-saffron" />
              <h2 className="overline-label text-ink-3">Members</h2>
            </div>
            <div className="grid grid-cols-3 gap-3 border border-line bg-surface p-5 text-center" data-testid="dash-members">
              <div><p className="font-serif-latin text-2xl text-ink">{u.total ?? "…"}</p><p className="text-xs text-ink-3">Total</p></div>
              <div><p className="font-serif-latin text-2xl text-ink">{u.new_30d ?? "…"}</p><p className="text-xs text-ink-3">New · 30d</p></div>
              <div><p className="font-serif-latin text-2xl text-ink">{u.banned ?? "…"}</p><p className="text-xs text-ink-3">Banned</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* Draft quick links */}
      {(d?.drafts_list || []).length > 0 && (
        <div className="mt-10">
          <div className="mb-4 flex items-center gap-3">
            <PenLine className="h-4 w-4 text-saffron" />
            <h2 className="overline-label text-ink-3">Pick up a draft</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="dash-drafts">
            {d.drafts_list.map((it) => (
              <Link key={it.id} to={`/admin/content/${it.id}`} className="group flex items-center justify-between gap-2 border border-line bg-surface px-4 py-3 transition-colors hover:border-saffron">
                <span className="min-w-0"><span className="block truncate text-sm text-ink">{it.title}</span><span className="text-xs text-ink-3">{it.type}</span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-saffron" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="mt-10">
        <div className="mb-4 flex items-center gap-3">
          <span className="h-px w-8 bg-[var(--gold)]" />
          <h2 className="overline-label text-ink-3">Manage</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="dash-quicklinks">
          {QUICK.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`quick-${q.to.replace(/\//g, "-").replace(/^-/, "")}`} className="group flex items-center gap-3 border border-line bg-surface px-4 py-3.5 transition-colors hover:border-saffron">
              <q.icon className="h-4.5 w-4.5 h-4 w-4 shrink-0 text-saffron" />
              <span className="text-sm text-ink-2 transition-colors group-hover:text-ink">{q.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
